import type { Candle } from '../domain/types'
import { mulberry32 } from '../lib/math/risk'

export function hashSeed(text: string): number {
  let h = 2166136261
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

export function gaussian(rng: () => number): number {
  let u = 0
  let v = 0
  while (u === 0) u = rng()
  while (v === 0) v = rng()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

export function addDays(isoDate: string, days: number): string {
  const d = new Date(isoDate + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

export interface SyntheticSeriesOptions {
  symbol: string
  startPrice: number
  days: number
  annualDrift?: number
  annualVol?: number
  startDate?: string
  seedSalt?: number
}

export function generateCandles(options: SyntheticSeriesOptions): Candle[] {
  const drift = options.annualDrift ?? 0.08
  const vol = options.annualVol ?? 0.3
  const days = options.days
  const dt = 1 / 252
  const rng = mulberry32(hashSeed(options.symbol) + (options.seedSalt ?? 0))
  const startDate = options.startDate ?? addDays(todayIso(), -days)

  const candles: Candle[] = []
  let prevClose = options.startPrice
  for (let i = 0; i < days; i++) {
    const z = gaussian(rng)
    const ret = (drift - 0.5 * vol * vol) * dt + vol * Math.sqrt(dt) * z
    const open = prevClose
    const close = Math.max(0.01, open * Math.exp(ret))
    const wiggle = Math.abs(gaussian(rng)) * vol * Math.sqrt(dt) * open
    const high = Math.max(open, close) + wiggle * 0.6
    const low = Math.min(open, close) - wiggle * 0.6
    const baseVol = 500000 + Math.floor(Math.abs(z) * 250000)
    candles.push({
      time: addDays(startDate, i),
      open: round2(open),
      high: round2(high),
      low: round2(Math.max(0.01, low)),
      close: round2(close),
      volume: baseVol,
    })
    prevClose = close
  }
  return candles
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}
